import React, { useState, useEffect } from 'react';
import { getRuote, generaSistema, generaSistemaRuota, giocataVeloce, salvaSistema, createGiocata } from '../services/api';

const Generatore = () => {
  const [ruote, setRuote] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sistema, setSistema] = useState(null);
  const [message, setMessage] = useState(null);
  
  // Form state
  const [budget, setBudget] = useState(50);
  const [tipo, setTipo] = useState('amboterno');
  const [strategia, setStrategia] = useState('mista');
  const [selectedRuote, setSelectedRuote] = useState([]);
  const [modalitaRuota, setModalitaRuota] = useState('tutte'); // 'tutte', 'singola', 'multiple'
  const [ruotaSingola, setRuotaSingola] = useState('');

  useEffect(() => {
    loadRuote();
  }, []);

  const loadRuote = async () => {
    try {
      const res = await getRuote();
      setRuote(res.data);
    } catch (err) {
      console.error('Errore caricamento ruote:', err);
    }
  };

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleGeneraSistema = async () => {
    setLoading(true);
    setSistema(null);
    
    try {
      let res;
      
      if (modalitaRuota === 'singola' && ruotaSingola) {
        res = await generaSistemaRuota({
          ruota_id: parseInt(ruotaSingola),
          budget,
          tipo
        });
      } else {
        res = await generaSistema({
          budget,
          tipo,
          strategia,
          ruote_ids: selectedRuote.length > 0 ? selectedRuote : null
        });
      }
      
      setSistema(res.data);
    } catch (err) {
      showMessage(err.response?.data?.detail || 'Errore generazione sistema', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleGiocataVeloce = async () => {
    setLoading(true);
    setSistema(null);
    
    try {
      const res = await giocataVeloce();
      setSistema({
        ...res.data,
        tipo: 'amboterno',
        costo: res.data.costo_stimato,
        ruote_nomi: res.data.ruote_suggerite,
        garanzia: res.data.motivazione
      });
    } catch (err) {
      showMessage('Errore generazione giocata veloce', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSalvaSistema = async () => {
    if (!sistema) return;
    
    try {
      await salvaSistema({
        numeri: sistema.numeri,
        ruote_ids: sistema.ruote_ids || [],
        tipo: sistema.tipo,
        nome: `Sistema ${sistema.strategia || 'generato'} - ${new Date().toLocaleDateString('it-IT')}`,
        costo: sistema.costo
      });
      showMessage('Sistema salvato!');
    } catch (err) {
      showMessage('Errore salvataggio', 'error');
    }
  };

  const handleGiocaOra = async () => {
    if (!sistema) return;
    
    // Trova ruote_ids
    let ruoteIds = sistema.ruote_ids;
    if (!ruoteIds || ruoteIds.length === 0) {
      // Se abbiamo solo i nomi, cerca gli ID
      ruoteIds = ruote
        .filter(r => sistema.ruote_nomi?.includes(r.nome))
        .map(r => r.id);
    }
    
    // Calcola importi in base al tipo
    let importoAmbo = 0, importoTerno = 0;
    if (sistema.tipo === 'ambo') {
      importoAmbo = sistema.costo;
    } else if (sistema.tipo === 'terno') {
      importoTerno = sistema.costo;
    } else if (sistema.tipo === 'amboterno') {
      // Dividi il budget tra ambo e terno
      importoAmbo = Math.round(sistema.costo / 2 * 100) / 100;
      importoTerno = sistema.costo - importoAmbo;
    }

    try {
      await createGiocata({
        numeri: sistema.numeri,
        ruote: ruoteIds,
        importo_ambo: importoAmbo,
        importo_terno: importoTerno,
        importo_quaterna: 0,
        importo_cinquina: 0,
        data_giocata: new Date().toISOString().split('T')[0]
      });
      showMessage('Giocata registrata!');
    } catch (err) {
      showMessage(err.response?.data?.detail || 'Errore registrazione giocata', 'error');
    }
  };

  const toggleRuota = (ruotaId) => {
    setSelectedRuote(prev =>
      prev.includes(ruotaId)
        ? prev.filter(r => r !== ruotaId)
        : [...prev, ruotaId]
    );
  };

  return (
    <div>
      <h2 style={{ marginBottom: '20px' }}>🎯 Generatore Sistemi</h2>

      {message && (
        <div className={`alert alert-${message.type === 'error' ? 'error' : 'success'}`}>
          {message.text}
        </div>
      )}

      <div className="grid grid-2">
        {/* Pannello Configurazione */}
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: '20px' }}>Configura Sistema</h3>

          <div className="form-group">
            <label className="form-label">Budget Massimo (€)</label>
            <input
              type="range"
              min="5"
              max="50"
              step="5"
              value={budget}
              onChange={(e) => setBudget(parseInt(e.target.value))}
              style={{ width: '100%' }}
            />
            <div style={{ textAlign: 'center', fontSize: '1.5rem', fontWeight: 'bold', color: '#2c5282' }}>
              €{budget}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Tipo Giocata</label>
            <select
              className="form-select"
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
            >
              <option value="ambo">Solo Ambo</option>
              <option value="terno">Solo Terno</option>
              <option value="amboterno">Ambo + Terno</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Strategia</label>
            <select
              className="form-select"
              value={strategia}
              onChange={(e) => setStrategia(e.target.value)}
            >
              <option value="mista">🎯 Mista (consigliata)</option>
              <option value="ritardi">⏰ Ritardatari</option>
              <option value="frequenze">📊 Frequenti</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Ruote</label>
            <select
              className="form-select"
              value={modalitaRuota}
              onChange={(e) => {
                setModalitaRuota(e.target.value);
                setSelectedRuote([]);
                setRuotaSingola('');
              }}
            >
              <option value="tutte">Tutte le ruote</option>
              <option value="singola">Ruota singola</option>
              <option value="multiple">Ruote selezionate</option>
            </select>
          </div>

          {modalitaRuota === 'singola' && (
            <div className="form-group">
              <label className="form-label">Seleziona Ruota</label>
              <select
                className="form-select"
                value={ruotaSingola}
                onChange={(e) => setRuotaSingola(e.target.value)}
              >
                <option value="">-- Seleziona --</option>
                {ruote.map(r => (
                  <option key={r.id} value={r.id}>{r.nome}</option>
                ))}
              </select>
            </div>
          )}

          {modalitaRuota === 'multiple' && (
            <div className="form-group">
              <label className="form-label">Seleziona Ruote</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {ruote.map(r => (
                  <span
                    key={r.id}
                    className="ruota-badge"
                    style={{
                      cursor: 'pointer',
                      background: selectedRuote.includes(r.id) ? '#2c5282' : '#e2e8f0',
                      color: selectedRuote.includes(r.id) ? 'white' : '#4a5568'
                    }}
                    onClick={() => toggleRuota(r.id)}
                  >
                    {r.nome}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button
              className="btn btn-primary"
              onClick={handleGeneraSistema}
              disabled={loading || (modalitaRuota === 'singola' && !ruotaSingola)}
              style={{ flex: 1 }}
            >
              {loading ? '⏳ Generazione...' : '🎰 Genera Sistema'}
            </button>
          </div>

          <div style={{ marginTop: '10px' }}>
            <button
              className="btn btn-secondary"
              onClick={handleGiocataVeloce}
              disabled={loading}
              style={{ width: '100%' }}
            >
              ⚡ Giocata Veloce
            </button>
          </div>
        </div>

        {/* Pannello Risultato */}
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: '20px' }}>Sistema Generato</h3>

          {!sistema ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
              <p style={{ fontSize: '3rem', marginBottom: '10px' }}>🎰</p>
              <p>Configura e genera un sistema</p>
            </div>
          ) : (
            <div>
              {/* Numeri */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ fontWeight: '600', display: 'block', marginBottom: '10px' }}>
                  Numeri Selezionati
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                  {sistema.numeri?.map(n => (
                    <span key={n} className="numero-ball">{n}</span>
                  ))}
                </div>
              </div>

              {/* Ruote */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ fontWeight: '600', display: 'block', marginBottom: '10px' }}>
                  Ruote
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                  {sistema.ruote_nomi?.map(r => (
                    <span key={r} className="ruota-badge">{r}</span>
                  ))}
                </div>
              </div>

              {/* Info */}
              <div style={{ background: '#f7fafc', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div>
                    <span style={{ color: '#718096' }}>Tipo:</span>
                    <strong style={{ marginLeft: '5px', textTransform: 'capitalize' }}>{sistema.tipo}</strong>
                  </div>
                  <div>
                    <span style={{ color: '#718096' }}>Costo:</span>
                    <strong style={{ marginLeft: '5px', color: '#2c5282' }}>€{sistema.costo}</strong>
                  </div>
                  {sistema.n_ambi > 0 && (
                    <div>
                      <span style={{ color: '#718096' }}>Ambi:</span>
                      <strong style={{ marginLeft: '5px' }}>{sistema.n_ambi}</strong>
                    </div>
                  )}
                  {sistema.n_terni > 0 && (
                    <div>
                      <span style={{ color: '#718096' }}>Terni:</span>
                      <strong style={{ marginLeft: '5px' }}>{sistema.n_terni}</strong>
                    </div>
                  )}
                </div>
              </div>

              {/* Garanzia */}
              <div className="alert alert-info" style={{ marginBottom: '20px' }}>
                💡 {sistema.garanzia}
              </div>
              {/* Azioni */}
              <div>
                <button className="btn btn-success" onClick={handleGiocaOra} style={{ width: '100%' }}>
                  Registra Giocata
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Dettaglio Combinazioni */}
      {sistema && (sistema.ambi?.length > 0 || sistema.terni?.length > 0) && (
        <div className="card" style={{ marginTop: '20px' }}>
          <h3 className="card-title" style={{ marginBottom: '20px' }}>Dettaglio Combinazioni</h3>
          
          <div className="grid grid-2">
            {sistema.ambi?.length > 0 && (
              <div>
                <h4 style={{ marginBottom: '10px' }}>Ambi (primi 10)</h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {sistema.ambi.map((ambo, idx) => (
                    <div key={idx} style={{ background: '#f7fafc', padding: '5px 10px', borderRadius: '6px' }}>
                      {ambo.join(' - ')}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {sistema.terni?.length > 0 && (
              <div>
                <h4 style={{ marginBottom: '10px' }}>Terni (primi 10)</h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {sistema.terni.map((terno, idx) => (
                    <div key={idx} style={{ background: '#f7fafc', padding: '5px 10px', borderRadius: '6px' }}>
                      {terno.join(' - ')}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Generatore;
